import { useLocalSearchParams } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { TabbioAIIcon } from '../common/TabbioAIIcon';
import { useTheme } from '../../hooks/useTheme';

const defaultAvatar = require('../../../assets/default-avatar.png');
import {
  CANDIDATES,
  SHORTLISTED,
  type CandidateAvailability,
} from '../../lib/candidates-mock-data';

function getAvailabilityLabelKey(availability: CandidateAvailability) {
  switch (availability) {
    case 'immediate':
      return 'candidates.filters.immediate';
    case '2weeks':
      return 'candidates.filters.twoWeeks';
    case '1month':
      return 'candidates.filters.oneMonth';
    case '3months':
      return 'candidates.filters.threeMonths';
    default:
      return 'candidates.filters.availability';
  }
}

export function CandidateDetailScreen() {
  const { t } = useTranslation('seeker');
  const { theme, isDark } = useTheme();
  const { id } = useLocalSearchParams<{ id: string }>();
  const candidate = CANDIDATES.find((item) => item.id === id);
  const shortlistEntry = SHORTLISTED.find((item) => item.id === id) ?? null;

  if (!candidate) return null;

  const cardBackground = isDark ? theme.surfaceElevated : 'rgba(255,255,255,0.82)';

  const profileFacts = [
    {
      label: t('candidates.experience'),
      value: candidate.experience,
    },
    {
      label: t('candidates.details.availability'),
      value: t(getAvailabilityLabelKey(candidate.availability)),
    },
    {
      label: t('candidates.details.source'),
      value: candidate.source,
    },
    {
      label: t('candidates.details.activity'),
      value: candidate.timeAgo,
    },
  ];

  return (
    <div className="h-full overflow-y-auto" style={{ paddingBlock: '20px 96px' }}>
      <div style={{ maxWidth: 760, marginInline: 'auto', width: '100%', paddingInline: 20 }}>
        <section
          style={{
            borderRadius: 32,
            border: '1px solid var(--tabbio-border-subtle)',
            backgroundColor: cardBackground,
            padding: 22,
            boxShadow: '0 10px 30px rgba(0,0,0,0.04)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              marginBottom: 18,
            }}
          >
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '7px 12px',
                borderRadius: 999,
                border: '1px solid var(--tabbio-border-subtle)',
                backgroundColor: 'var(--tabbio-surface)',
                color: 'var(--tabbio-text-secondary)',
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              <TabbioAIIcon size={12} />
              {t('candidates.match', { value: candidate.match })}
            </span>

            {shortlistEntry ? (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '7px 12px',
                  borderRadius: 999,
                  border: '1px solid var(--tabbio-border-subtle)',
                  backgroundColor: 'var(--tabbio-surface)',
                  color: 'var(--tabbio-text-secondary)',
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                {t(`candidates.status.${shortlistEntry.status}` as never)}
              </span>
            ) : null}
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 18,
            }}
          >
            <img
              src={defaultAvatar}
              alt=""
              style={{
                width: 76,
                height: 76,
                borderRadius: 999,
                flexShrink: 0,
                objectFit: 'cover',
                border: '1px solid var(--tabbio-border-subtle)',
                background: 'var(--tabbio-surface-secondary)',
              }}
            />

            <div style={{ flex: 1, minWidth: 0 }}>
              <h1
                style={{
                  margin: 0,
                  fontSize: 32,
                  lineHeight: '36px',
                  fontWeight: 700,
                  letterSpacing: '-0.03em',
                  color: 'var(--tabbio-text)',
                }}
                dir="auto"
              >
                {candidate.name}
              </h1>
              <div
                style={{
                  marginTop: 6,
                  fontSize: 15,
                  fontWeight: 500,
                  color: 'var(--tabbio-text-secondary)',
                }}
              >
                {candidate.title} · {candidate.location}
              </div>
              <p
                style={{
                  margin: '10px 0 0',
                  fontSize: 14,
                  lineHeight: '20px',
                  color: 'var(--tabbio-text-tertiary)',
                  maxWidth: 560,
                }}
                dir="auto"
              >
                {candidate.bio}
              </p>
            </div>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: 12,
              marginTop: 20,
            }}
          >
            {profileFacts.map((fact) => (
              <div
                key={fact.label}
                style={{
                  minHeight: 88,
                  borderRadius: 22,
                  border: '1px solid var(--tabbio-border-subtle)',
                  backgroundColor: 'var(--tabbio-surface)',
                  padding: 14,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    color: 'var(--tabbio-text-tertiary)',
                  }}
                >
                  {fact.label}
                </div>
                <div
                  style={{
                    fontSize: 15,
                    lineHeight: '20px',
                    fontWeight: 600,
                    color: 'var(--tabbio-text)',
                  }}
                >
                  {fact.value}
                </div>
              </div>
            ))}
          </div>
        </section>

        {shortlistEntry ? (
          <section
            style={{
              marginTop: 16,
              borderRadius: 24,
              border: '1px solid var(--tabbio-border-subtle)',
              backgroundColor: 'var(--tabbio-surface)',
              padding: 16,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <div
              style={{
                width: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <TabbioAIIcon size={14} />
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--tabbio-text)' }}>
                {t('candidates.shortlistedAction')}
              </div>
              <div style={{ marginTop: 4, fontSize: 13, color: 'var(--tabbio-text-secondary)' }}>
                {t('candidates.shortlistedAgo', { time: shortlistEntry.shortlistedAgo })}
              </div>
            </div>
          </section>
        ) : null}

        <section style={{ marginTop: 22 }}>
          <h3
            style={{
              margin: '0 0 10px',
              fontSize: 16,
              fontWeight: 600,
              color: 'var(--tabbio-text)',
            }}
          >
            {t('candidates.skills')}
          </h3>
          <div
            style={{
              borderRadius: 28,
              border: '1px solid var(--tabbio-border-subtle)',
              backgroundColor: cardBackground,
              padding: 18,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
            }}
          >
            {candidate.skills.map((skill) => (
              <span
                key={skill}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '8px 12px',
                  borderRadius: 999,
                  border: '1px solid var(--tabbio-border-subtle)',
                  backgroundColor: 'var(--tabbio-surface)',
                  color: 'var(--tabbio-text-secondary)',
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {skill}
              </span>
            ))}
          </div>
        </section>

        <section style={{ marginTop: 22 }}>
          <h3
            style={{
              margin: '0 0 10px',
              fontSize: 16,
              fontWeight: 600,
              color: 'var(--tabbio-text)',
            }}
          >
            {t('candidates.about')}
          </h3>
          <div
            style={{
              borderRadius: 28,
              border: '1px solid var(--tabbio-border-subtle)',
              backgroundColor: cardBackground,
              padding: 18,
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: 15,
                lineHeight: '24px',
                color: 'var(--tabbio-text-secondary)',
                whiteSpace: 'pre-wrap',
              }}
              dir="auto"
            >
              {candidate.bio}
            </p>
          </div>
        </section>

        <div style={{ marginTop: 22 }}>
          <button
            className="w-full py-3.5 rounded-full border-0 cursor-pointer text-[16px] font-semibold transition-opacity hover:opacity-90"
            style={{
              backgroundColor: shortlistEntry
                ? 'var(--tabbio-button-primary-bg)'
                : 'var(--tabbio-button-primary-bg)',
              color: '#fff',
              border: '1px solid var(--tabbio-button-primary-bg)',
              fontFamily: 'inherit',
            }}
          >
            {shortlistEntry ? t('candidates.shortlistedAction') : t('candidates.shortlist')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CandidateDetailScreen;
